// ─────────────────────────────────────────────────────────────────────────────
// lib/releases, data layer for the Releases hub.
//
// SOURCE: GET /api/data/releases (AUTH_WORKER). The worker's handleGetUserData
// returns, verbatim:
//     return jsonResponse({ success: true, key: dataKey, data: data || {} });
// where `data` is the band's stored releases blob (an array, or { value: [...] }).
//
// The unwrap + every derivation below is ported 1:1 from the legacy working
// caller site/js/releaseshub-wiring-v2.js (fetchReleases / isReleased /
// isInCycle / isDraft / statusKey / openTasks / cycleLabel / pickFeatured /
// the fmt* helpers) so the React hub computes identical values.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { apiGet, apiPut } from './api';
import { getToken } from './auth';
import { useCachedData } from './dataCache';

// ── Release shape (only the fields the hub reads) ─────────────────────────────
export interface ChecklistItem {
  readonly id?: string;
  readonly phase?: string;
  readonly label?: string;
  readonly task?: string;
  readonly done?: boolean;
  readonly completed?: boolean;
  readonly status?: string;
  readonly date?: string;
  readonly due?: string;
  readonly due_date?: string;
  readonly assignee?: string | { firstName?: string; name?: string; email?: string; avatar_url?: string; avatarUrl?: string; photo_url?: string };
  readonly assignees?: ReadonlyArray<{ firstName?: string; name?: string; email?: string; avatar_url?: string }>;
}
export interface AssetItem {
  readonly id?: string;
  readonly kind?: 'text' | 'file' | 'link' | string;
  readonly name?: string;
  readonly state?: string;   // 'in' | 'wip' | …
  readonly url?: string;
  readonly text?: string;
}
export interface WriterCredit { readonly name?: string; readonly pro?: string; readonly ipi?: string; readonly split?: string }
export interface MusicianCredit { readonly name?: string; readonly instruments?: string }
export interface CodeEntry { readonly label?: string; readonly code?: string; readonly status?: string }
/** Registration-status options (verbatim from release-focused-wiring.js CODE_STATUS). */
export const CODE_STATUS: readonly string[] = ['', 'Not submitted', 'Submitted', 'Registered', 'Live'];
/** Code-label datalist suggestions (verbatim from the legacy rpCodeLabels datalist). */
export const CODE_LABEL_SUGGESTIONS: readonly string[] = [
  'Album UPC', 'Track 1', 'Track 2', 'Track 3', 'Dolby ATMOS', 'Radio Edit', 'Clean', 'Explicit',
  'Instrumental', 'Acapella', 'Visualizer', 'Sped Up', 'Slowed', 'Acoustic', 'Extended', 'Remix',
];
export interface ReleaseDetails {
  readonly artist?: string;
  readonly co_artist?: string;
  readonly label?: string;
  readonly copyright?: string;
  readonly publisher?: string;
  readonly pro?: string;
  readonly isrc?: string;
  readonly producer?: string;
  readonly engineer?: string;
  readonly mixed_by?: string;
  readonly mastered_by?: string;
  readonly studio?: string;
  readonly writers?: ReadonlyArray<WriterCredit>;
  readonly musicians?: ReadonlyArray<MusicianCredit>;
  readonly codes?: ReadonlyArray<CodeEntry>;
}
export interface TimelineNode { readonly id?: string; readonly date?: string; readonly name?: string; readonly desc?: string; readonly state?: string }
/** One manual budget line (release-budget-r2 2026-07-05). `amount` is USD and
 *  may carry cents; `paidOn` is an ISO date (empty/absent = not paid yet).
 *  `planned`/`spent` are the r1 fields (release-budget 2026-07-05), kept in
 *  the type so existing rows still parse; readers migrate amount = spent ?? planned. */
export interface BudgetItem {
  readonly id: string;
  readonly label: string;
  readonly service?: string;
  readonly amount?: number;
  readonly paidOn?: string;
  /** legacy r1 field, tolerated on read, no longer written by the UI */
  readonly planned?: number;
  /** legacy r1 field, tolerated on read, no longer written by the UI */
  readonly spent?: number;
}
export interface Release {
  readonly id?: string | number;
  readonly name?: string;
  readonly type?: string;
  readonly release_date?: string;
  readonly draft?: boolean;
  readonly unreleased?: boolean;
  readonly artwork?: string;
  readonly label_name?: string;
  readonly track_count?: number;
  readonly tracks?: ReadonlyArray<unknown>;
  readonly checklist?: ReadonlyArray<ChecklistItem>;
  readonly assets?: ReadonlyArray<AssetItem>;
  readonly smartlink_clicks_mtd?: number;
  readonly smart_link_clicks?: number;
  readonly ad_spend?: number;
  readonly smartlink_url?: string;
  readonly smart_link?: string;
  readonly public_url?: string;
  readonly bio?: string;
  /** release-wire 2026-07-19 (Ryan): THE TikTok moment, one timestamp +
   *  note, set on Overview, surfaces on the public release page. */
  readonly tiktok_spot?: { readonly at?: string; readonly note?: string };
  /** release-audio 2026-07-19: uploaded songs. One row per song; files[0]
   *  is the playable primary (Master first), Atmos deliverables are the
   *  variants behind the dropdown. Keys are server-side R2 paths. */
  readonly audio_tracks?: ReadonlyArray<{
    readonly id: string;
    readonly title?: string;
    readonly files?: ReadonlyArray<{
      readonly id: string; readonly name?: string; readonly size?: number;
      readonly type?: string; readonly kind?: string; readonly key?: string;
    }>;
  }>;
  readonly created_at?: string;
  readonly details?: ReleaseDetails;
  readonly timeline?: ReadonlyArray<TimelineNode>;
  /** Budget (release-budget-r2 2026-07-05): overall total + funding source +
   *  manual line items. Live Meta ad spend is NOT stored here, it's joined at
   *  render time from fetchAdSpendByRelease(). */
  readonly budget?: { readonly total?: number; readonly source?: string; readonly items?: ReadonlyArray<BudgetItem> };
  /** Per-DSP deep links shown in the smart-link phone preview. */
  readonly links?: { readonly spotify?: string; readonly apple_music?: string; readonly amazon_music?: string; readonly youtube_music?: string };
}

interface ReleasesResponse { readonly success?: boolean; readonly key?: string; readonly data?: unknown; readonly value?: unknown }

/** GET /api/data/releases → Release[]. Unwrap mirrors releaseshub-wiring-v2.js
 *  fetchReleases() exactly:
 *    var raw = json && (json.value !== undefined ? json.value
 *            : (json.data !== undefined ? json.data : json));
 *    if (raw && Array.isArray(raw.value)) raw = raw.value;
 *    if (!Array.isArray(raw)) raw = [];
 */
export async function fetchReleases(): Promise<Release[]> {
  let json: ReleasesResponse | null = null;
  try {
    json = await apiGet<ReleasesResponse>('auth', '/api/data/releases');
  } catch {
    return [];
  }
  let raw: unknown =
    json && (json.value !== undefined ? json.value : (json.data !== undefined ? json.data : json));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) raw = [];
  return raw as Release[];
}

// ── formatters (verbatim from releaseshub-wiring-v2.js) ───────────────────────
export function safeNum(n: unknown, fallback?: number): number {
  const v = Number(n);
  return isFinite(v) ? v : (fallback == null ? 0 : fallback);
}
/** 9,210 → "9.2k", 1,200,000 → "1.2M". */
export function fmtInt(n: unknown): string {
  const v = safeNum(n);
  if (v >= 1e6) return (Math.round(v / 1e5) / 10).toString().replace(/\.0$/, '') + 'M';
  if (v >= 1e3) return (Math.round(v / 1e2) / 10).toString().replace(/\.0$/, '') + 'k';
  return String(v);
}
export function fmtIntFull(n: unknown): string {
  return String(safeNum(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
export function fmtMoney(n: unknown): string {
  const v = safeNum(n);
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
export function fmtDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
export function fmtDateShort(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
export function daysFromNow(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - today.getTime()) / 86400000);
}
export function typeLabel(t?: string): string {
  const v = String(t || 'single').toLowerCase();
  if (v === 'ep') return 'EP';
  if (v === 'album') return 'Album';
  return 'Single';
}

// ── status classifier (verbatim) ─────────────────────────────────────────────
export function isReleased(r: Release): boolean {
  if (!r || !r.release_date || r.draft) return false;
  if (r.unreleased) return false;
  const d = new Date(r.release_date + 'T12:00:00');
  return !isNaN(d.getTime()) && d <= new Date();
}
export function isInCycle(r: Release): boolean {
  if (!r) return false;
  if (r.draft) return false;
  if (r.unreleased) return true;
  if (!r.release_date) return false;
  if (isReleased(r)) return false;
  const dt = new Date(r.release_date + 'T12:00:00');
  if (isNaN(dt.getTime())) return false;
  const diff = daysFromNow(r.release_date);
  return diff != null && diff > -90;
}
export function isDraft(r: Release): boolean { return !!(r && r.draft); }
export type StatusKey = 'draft' | 'out' | 'cycle';
export function statusKey(r: Release): StatusKey {
  if (isDraft(r)) return 'draft';
  if (isReleased(r)) return 'out';
  return 'cycle';
}

// ── checklist / prep (verbatim) ──────────────────────────────────────────────
export function openTasks(r: Release): ChecklistItem[] {
  if (!r || !Array.isArray(r.checklist)) return [];
  return (r.checklist as ChecklistItem[]).filter((t) => {
    const closed = (t.done === true || t.completed === true) ||
      (!!t.status && /^(done|complete|completed|closed)$/i.test(String(t.status)));
    return !closed;
  });
}
export function dueClass(due?: string): string {
  if (!due) return '';
  const diff = daysFromNow(due);
  if (diff == null) return '';
  if (diff < 0) return 'overdue';
  if (diff <= 7) return 'warn';
  return '';
}
/** "Day N · T-NN", N = days since cycle start (release_date − 90d), NN = days to release. */
export function cycleLabel(r: Release): string {
  const rd = r && r.release_date;
  if (!rd) return '';
  const days = daysFromNow(rd);
  if (days == null) return '';
  let dayN = 90 - days; if (dayN < 1) dayN = 1;
  if (days > 0) return 'Day ' + dayN + ' · T‑' + days;
  if (days === 0) return 'Day ' + dayN + ' · TODAY';
  return 'Day ' + dayN + ' · OUT';
}

// ── featured picker (verbatim) ───────────────────────────────────────────────
export function pickFeatured(rs: readonly Release[]): Release | null {
  const cycle = rs.filter(isInCycle).slice().sort((a, b) =>
    (Date.parse((a.release_date || '') + 'T00:00:00') || 0) - (Date.parse((b.release_date || '') + 'T00:00:00') || 0));
  if (cycle.length) return cycle[0];
  const out = rs.filter(isReleased).slice().sort((a, b) =>
    (Date.parse((b.release_date || '') + 'T00:00:00') || 0) - (Date.parse((a.release_date || '') + 'T00:00:00') || 0));
  if (out.length) return out[0];
  return null;
}

// ── per-release derived counts (mirror renderFeatured / renderGrid) ───────────
export function checklistProgress(r: Release): { done: number; total: number } {
  const cl = Array.isArray(r.checklist) ? r.checklist : [];
  return { done: cl.filter((x) => x.done).length, total: cl.length };
}
/** Expected-asset template per release type, verbatim port of
 *  release-focused-wiring.js EXPECTED_ASSETS_BY_TYPE. Each row's kind/name/
 *  destLabel/isCover/motion are the definition; saved r.assets only carry
 *  url/text/state (the definition wins for everything else). */
export interface ExpectedAsset {
  readonly id: string;
  readonly name: string;
  readonly kind: 'file' | 'link' | 'text';
  readonly destLabel?: string;
  readonly isCover?: boolean;
  readonly motion?: boolean;
}
export const EXPECTED_ASSETS_BY_TYPE: Record<'single' | 'ep' | 'album', readonly ExpectedAsset[]> = {
  single: [
    { id: 'cover-3000', name: 'Cover artwork · 3000×3000', kind: 'file', isCover: true },
    { id: 'lyrics', name: 'Lyrics', kind: 'text' },
    { id: 'music-video', name: 'Music video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
    { id: 'lyric-video', name: 'Lyric video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
  ],
  ep: [
    { id: 'cover-3000', name: 'Cover artwork · 3000×3000', kind: 'file', isCover: true },
    { id: 'lyrics', name: 'Lyrics · all tracks', kind: 'text' },
    { id: 'tracklist', name: 'Tracklist + credits PDF', kind: 'link', destLabel: 'drive.google.com/…' },
    { id: 'music-video', name: 'Music video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
    { id: 'lyric-video', name: 'Lyric video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
  ],
  album: [
    { id: 'cover-3000', name: 'Cover artwork · 3000×3000', kind: 'file', isCover: true },
    { id: 'lyrics', name: 'Lyrics · all tracks', kind: 'text' },
    { id: 'tracklist', name: 'Tracklist + credits booklet', kind: 'link', destLabel: 'drive.google.com/…' },
    { id: 'vinyl-art', name: 'Vinyl jacket art (gatefold)', kind: 'link', destLabel: 'drive.google.com/…' },
    { id: 'music-video', name: 'Music video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
    { id: 'lyric-video', name: 'Lyric video', kind: 'link', destLabel: 'youtu.be/…', motion: true },
  ],
};
/** Old slots we no longer render (data isn't deleted; just hidden),
 *  verbatim from release-focused-wiring.js DEPRECATED_ASSET_IDS. */
export const DEPRECATED_ASSET_IDS: Record<string, 1> = {
  /* release-audio 2026-07-19 (Ryan: "kill the master thing as a default")
    , uploaded Songs replace the master link slots; old saved rows hide. */
  'master': 1,
  'masters': 1,
  /* release-media-upload 2026-07-19: direct uploads replace these links. */
  'canvas': 1,
  'press-photos-drv': 1,
  'cover-square': 1,
  'cover-9-16': 1,
  'masters-vault': 1,
  'press-photos': 1, // superseded by press-photos-drv
};

/** A merged asset row, the expected definition overlaid with saved url/text/
 *  state, plus any custom saved rows appended. Extends AssetItem with the
 *  presentational fields the row renderer needs. */
export interface MergedAsset extends AssetItem {
  readonly id: string;
  readonly kind: 'text' | 'file' | 'link' | string;
  readonly name: string;
  readonly destLabel?: string;
  readonly motion?: boolean;
  readonly isCover?: boolean;
}

/** Merge the expected-by-type template with the release's saved assets,
 *  verbatim port of release-focused-wiring.js paintAssets() merge logic.
 *  Expected definition wins for kind/name/destLabel/motion/isCover; saved data
 *  only carries url/text/state. The cover row auto-fills url from r.artwork
 *  when nothing's been saved. Custom saved rows (not expected, not deprecated)
 *  are appended. */
export function mergeReleaseAssets(r: Release): MergedAsset[] {
  const saved = Array.isArray(r.assets) ? r.assets : [];
  const savedById: Record<string, AssetItem> = {};
  for (const a of saved) { if (a && a.id) savedById[a.id] = a; }
  const type = String(r.type || 'single').toLowerCase();
  const expected = EXPECTED_ASSETS_BY_TYPE[type as 'single' | 'ep' | 'album'] || EXPECTED_ASSETS_BY_TYPE.single;

  const rows: MergedAsset[] = expected.map((e) => {
    const s = savedById[e.id] || {};
    let url = s.url;
    if (!url && e.isCover && r.artwork) url = r.artwork;
    return {
      id: e.id, kind: e.kind, name: e.name,
      destLabel: e.destLabel, motion: e.motion, isCover: e.isCover,
      url, text: s.text, state: s.state,
    };
  });
  // Append custom user-added rows (not expected, not deprecated).
  for (const a of saved) {
    if (!a || !a.id) continue;
    if (DEPRECATED_ASSET_IDS[a.id]) continue;
    if (expected.find((x) => x.id === a.id)) continue;
    rows.push({ id: a.id, kind: a.kind || 'file', name: a.name || a.id, url: a.url, text: a.text, state: a.state });
  }
  return rows;
}

/** Row state, verbatim port of release-focused-wiring.js assetState(). Text
 *  rows are 'in' when they carry text OR a url; other rows are 'in' when they
 *  carry a url or an explicit in/uploaded state; otherwise 'wip'. ('overdue'
 *  is retained for parity though the merged rows here never carry a due date.) */
export function assetRowState(a: AssetItem): 'in' | 'wip' | 'overdue' {
  if (a.kind === 'text') {
    const hasText = !!(a.text && String(a.text).trim());
    return (hasText || a.url) ? 'in' : 'wip';
  }
  if (a.url || a.state === 'in' || (a as { uploaded?: boolean }).uploaded) return 'in';
  if (a.state === 'wip' || a.state === 'review') return 'wip';
  return 'wip';
}

/** Shared asset-progress counter, single source of truth so the Releases-hub
 *  cards show the SAME "in / total" as the Asset Library (e.g. 3/7). Verbatim
 *  port of release-focused-wiring.js computeAssetProgressForRelease(): builds
 *  the expected-by-type template + saved states + custom rows (skipping
 *  deprecated ids), then counts with assetRowState. Keeps the existing
 *  {done,total} shape (in → done) so callers keep working. */
export function assetProgress(r: Release): { done: number; total: number } {
  const rows = mergeReleaseAssets(r);
  const done = rows.filter((a) => assetRowState(a) === 'in').length;
  return { done, total: rows.length };
}

// ── deterministic fallback gradient (verbatim palette + hash) ─────────────────
export const AC_GRADIENTS: readonly string[] = [
  'linear-gradient(160deg, #b03048 0%, #6a1426 35%, #2a0a14 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #a85540 0%, #5a2418 35%, #2a1208 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #c87842 0%, #7a3a18 35%, #3a1a08 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #8a3050 0%, #4a1428 35%, #240a14 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #6e2840 0%, #3a1224 35%, #1a0810 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #a83a2a 0%, #5a1a10 35%, #2a0a08 70%, #0e0c0b 100%)',
  'linear-gradient(160deg, #946846 0%, #4a3220 35%, #241810 70%, #0e0c0b 100%)',
];
export function gradientFor(name?: string): string {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) - hash) + s.charCodeAt(i);
  return AC_GRADIENTS[Math.abs(hash) % AC_GRADIENTS.length];
}

/** Resolve a release's artwork URL, verbatim port of release-focused-wiring.js
 *  resolveArtworkUrl(): prefer the Asset Library's cover upload (cover-3000 →
 *  cover-square → cover-9-16), then fall back to the legacy r.artwork field. */
export function resolveArtworkUrl(r: Release): string {
  if (!r) return '';
  const assets = Array.isArray(r.assets) ? r.assets : [];
  const byId: Record<string, AssetItem> = {};
  for (const a of assets) { if (a && a.id) byId[a.id] = a; }
  for (const id of ['cover-3000', 'cover-square', 'cover-9-16']) {
    const a = byId[id];
    if (a && a.url) return freshStreamUrl(a.url);
  }
  return r.artwork || '';
}

/** asset-clobber 2026-08-22 — uploaded assets store an audio-stream URL that
 *  embeds the UPLOADER's session token (sessions die at 48h, and another seat
 *  viewing the release must not ride the uploader's token). At render time,
 *  swap in the CURRENT viewer's token so the URL is always fresh and always
 *  theirs; the worker re-gates access per token. Non-stream URLs pass through
 *  untouched. */
export function freshStreamUrl(u: string): string {
  const s = String(u || '');
  if (s.indexOf('/api/releases/audio-stream') < 0) return s;
  try {
    const url = new URL(s);
    url.searchParams.set('token', getToken() || '');
    return url.toString();
  } catch { return s; }
}

/** Background CSS for a cover, resolved artwork image if present, else the
 *  deterministic gradient fallback. */
export function coverBackground(r: Release): string {
  const art = resolveArtworkUrl(r);
  if (art) return "url('" + String(art).replace(/'/g, "\\'") + "') center / cover no-repeat";
  return gradientFor(r.name);
}

/** URL slug for the detail route (mirrors legacy slugify). */
export function slugify(s?: string): string {
  return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** slug-dedupe 2026-07-05 (Ryan): per-band unique detail slug, the pretty
 *  name-slug when the title is unique in this band's list, otherwise
 *  name-slug + '-' + id so two releases with the same title stay reachable.
 *  (Cross-band collisions were never possible: the list is band-scoped.) */
export function slugFor(r: Release, all: readonly Release[]): string {
  const base = slugify(r.name) || String(r.id || '');
  const dupes = all.filter((x) => slugify(x.name) === slugify(r.name));
  return dupes.length > 1 ? base + '-' + String(r.id) : base;
}

/** Resolve a release from the list by its detail-route slug: unique name-slug,
 *  exact id, id-suffixed dupe slug, falling back to first name match so old
 *  bare links to a now-duplicated title still land somewhere sane. */
export function findReleaseBySlug(rs: readonly Release[], slug: string): Release | null {
  if (!slug) return null;
  const byName = rs.filter((r) => slugify(r.name) === slug);
  if (byName.length === 1) return byName[0];
  const byId = rs.find((r) => String(r.id) === slug);
  if (byId) return byId;
  const suffixed = rs.find((r) => slug === slugify(r.name) + '-' + String(r.id));
  if (suffixed) return suffixed;
  return byName[0] || null;
}

/** Canonical public landing URL, artistcntrl.com/<band>/<release> (mirrors
 *  buildLandingUrl in releaseshub-wiring-v2.js / marketing-wiring.js). */
export function landingUrl(bandName: string, r: Release): string {
  const b = slugify(bandName), rel = slugify(r.name);
  if (r.smart_link) return String(r.smart_link).replace(/^https?:\/\//, '');
  if (r.public_url) return String(r.public_url).replace(/^https?:\/\//, '');
  return 'artistcntrl.com/' + (b ? b + '/' : '') + rel;
}

// ── smart-link analytics (stats worker) ──────────────────────────────────────
// GET stats:/release-analytics?slug=<band>/<release>. Blob shape (mirrors
// releaseshub-wiring-v2.js deriveStats + renderDspBars):
//   { total, daily:{'YYYY-MM-DD':visits},
//     clicks:{ total, by_platform:{spotify,apple_music,youtube_music,amazon_music} } }
export interface ReleaseAnalytics {
  readonly total?: number;
  readonly daily?: Record<string, number>;
  readonly clicks?: { readonly total?: number; readonly by_platform?: Record<string, number> };
}
export interface DerivedStats {
  readonly visitsMtd: number;
  readonly visitsLifetime: number;
  readonly clicksLifetime: number;
  readonly conv: number;
  readonly byPlatform: Record<string, number>;
}

/** Verbatim port of releaseshub-wiring-v2.js deriveStats(). */
export function deriveStats(a: ReleaseAnalytics | null): DerivedStats {
  if (!a) return { visitsMtd: 0, visitsLifetime: 0, clicksLifetime: 0, conv: 0, byPlatform: {} };
  const t = new Date();
  const monthStart = new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
  let visitsMtd = 0;
  const daily = a.daily || {};
  for (const d of Object.keys(daily)) { if (d >= monthStart) visitsMtd += Number(daily[d]) || 0; }
  const visitsLifetime = Number(a.total) || 0;
  const clicksLifetime = Number((a.clicks && a.clicks.total) || 0);
  const conv = visitsLifetime > 0 ? Math.round(clicksLifetime / visitsLifetime * 100) : 0;
  const byPlatform = (a.clicks && a.clicks.by_platform) || {};
  return { visitsMtd, visitsLifetime, clicksLifetime, conv, byPlatform };
}

/** DSP breakdown rows (counts + pct + bar width), mirrors renderDspBars(). */
export interface DspBar { readonly key: 'spot' | 'apl' | 'ytm' | 'amz'; readonly count: number; readonly pct: number; readonly barPct: number }
export function dspBars(byPlatform: Record<string, number>): DspBar[] {
  const bp = byPlatform || {};
  const raw: { key: DspBar['key']; count: number }[] = [
    { key: 'spot', count: safeNum(bp.spotify ?? bp.spot ?? 0) },
    { key: 'apl', count: safeNum(bp.apple_music ?? bp.apple ?? bp.apl ?? 0) },
    { key: 'ytm', count: safeNum(bp.youtube_music ?? bp.youtube ?? bp.ytm ?? 0) },
    { key: 'amz', count: safeNum(bp.amazon_music ?? bp.amazon ?? bp.amz ?? 0) },
  ];
  const totalClicks = raw.reduce((s, d) => s + d.count, 0);
  const max = Math.max(...raw.map((d) => d.count), 1);
  return raw.map((d) => ({
    key: d.key,
    count: d.count,
    pct: totalClicks ? (d.count / totalClicks * 100) : 0,
    barPct: max ? (d.count / max * 100) : 0,
  }));
}

interface AnalyticsResponse { readonly data?: ReleaseAnalytics }
/** Fetch one release's analytics. Slug = slug(band)/slug(release); unwrap is
 *  `json.data ?? json` exactly like fetchReleaseAnalytics in the legacy hub. */
export async function fetchReleaseAnalytics(bandName: string, r: Release): Promise<ReleaseAnalytics | null> {
  const bandSlug = slugify(bandName);
  const relSlug = slugify(r.name);
  if (!bandSlug || !relSlug) return null;
  let json: (ReleaseAnalytics & AnalyticsResponse) | null = null;
  try {
    json = await apiGet<ReleaseAnalytics & AnalyticsResponse>(
      'stats', '/release-analytics?slug=' + encodeURIComponent(bandSlug + '/' + relSlug));
  } catch { return null; }
  if (!json) return null;
  return (json.data !== undefined ? json.data : json) as ReleaseAnalytics;
}

// ── ad spend per release (Meta) ───────────────────────────────────────────────
// audit 2026-07-02: rebuilt to mirror the WORKING mobile flow (MobileReleases
// fetchAdSpend): the insights endpoint REQUIRES ?account=<id> (first account
// from /api/meta/ads/accounts), answers { success, rows:[{ campaign_id, spend }] }
//, not { campaigns:[{ id }] }, and meta_campaign_tags values are
// { release_id } records (or legacy plain ids). The old call omitted the
// account, parsed a shape the worker never returns, and mis-read the tag map,
// so the desktop ad-spend tile was permanently "-".
type CampaignTagRec = { release_id?: string | number } | string | number;
export async function fetchAdSpendByRelease(): Promise<Record<string, number>> {
  // 1) Tag map: campaign_id → { release_id } | plain release id. The worker
  //    wraps stored data as { success, key, data:<stored> }; the stored blob
  //    may itself be { value: <map> } (legacy PUT shape), peel both.
  const tagsRaw = await apiGet<{ data?: unknown; value?: unknown }>('auth', '/api/data/meta_campaign_tags').catch(() => null);
  let tags: unknown = tagsRaw && (tagsRaw.data !== undefined ? tagsRaw.data : tagsRaw);
  if (tags && typeof tags === 'object' && (tags as { value?: unknown }).value !== undefined) tags = (tags as { value?: unknown }).value;
  const tagMap: Record<string, CampaignTagRec> =
    tags && typeof tags === 'object' && !Array.isArray(tags) ? (tags as Record<string, CampaignTagRec>) : {};
  if (!Object.keys(tagMap).length) return {};

  // 2) First ad account → per-campaign lifetime insights (preset=maximum).
  const acct = await apiGet<{ success?: boolean; accounts?: Array<{ id?: string }> }>('auth', '/api/meta/ads/accounts').catch(() => null);
  const accounts = (acct && acct.success && acct.accounts) || [];
  const accountId = Array.isArray(accounts) && accounts.length ? accounts[0]?.id : undefined;
  if (!accountId) return {};
  const ins = await apiGet<{ success?: boolean; rows?: Array<{ campaign_id?: string | number; spend?: number }> }>(
    'auth', '/api/meta/ads/insights?account=' + encodeURIComponent(accountId) + '&preset=maximum&level=campaign').catch(() => null);
  if (!ins || !ins.success || !Array.isArray(ins.rows)) return {};
  const byCampaign: Record<string, number> = {};
  for (const row of ins.rows) { if (row && row.campaign_id != null) byCampaign[String(row.campaign_id)] = safeNum(row.spend || 0); }

  // 3) Join campaign spend onto releases through the tag map.
  const byRelease: Record<string, number> = {};
  for (const campId of Object.keys(tagMap)) {
    const rec = tagMap[campId];
    const rid = rec != null && typeof rec === 'object' ? rec.release_id : rec;
    if (rid == null || rid === '') continue;
    byRelease[String(rid)] = (byRelease[String(rid)] || 0) + (byCampaign[campId] || 0);
  }
  return byRelease;
}

/** Enrichment hook, fetches per-release analytics (parallel) + ad spend once.
 *  Keyed by String(release.id). Each resolves independently; the map updates
 *  as results land so the UI fills in progressively. */
export function useReleaseEnrichment(
  releases: readonly Release[],
  bandName: string,
): { statsById: Record<string, DerivedStats>; adSpendById: Record<string, number> } {
  const [statsById, setStatsById] = useState<Record<string, DerivedStats>>({});
  const [adSpendById, setAdSpendById] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    if (!releases.length) return;
    if (bandName) {
      for (const r of releases) {
        const rid = String(r.id || '');
        if (!rid) continue;
        void fetchReleaseAnalytics(bandName, r).then((a) => {
          if (!alive || !a) return;
          setStatsById((m) => ({ ...m, [rid]: deriveStats(a) }));
        });
      }
    }
    void fetchAdSpendByRelease().then((m) => { if (alive) setAdSpendById(m); });
    return () => { alive = false; };
  }, [releases, bandName]);
  return { statsById, adSpendById };
}

/** Shared unwrap → Release[] for the read-modify-write helpers. */
async function readReleasesArray(): Promise<Release[]> {
  let json: ReleasesResponse | null = null;
  try { json = await apiGet<ReleasesResponse>('auth', '/api/data/releases'); } catch { return []; }
  let raw: unknown = json && (json.value !== undefined ? json.value : (json.data !== undefined ? json.data : json));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) raw = [];
  return raw as Release[];
}

/** Persist an edited release, read-modify-write the whole blob so other
 *  releases aren't clobbered (verbatim port of release-focused-wiring.js
 *  persistRelease(): GET /api/data/releases → replace by id → PUT {value}). */
export async function persistRelease(updated: Release): Promise<boolean> {
  const arr = await readReleasesArray();
  const idx = arr.findIndex((x) => String(x.id) === String(updated.id));
  if (idx >= 0) {
    /* asset-clobber 2026-08-22 (Ryan: cover upload "showing blank") — the
       upload endpoints persist media entries (audio_tracks / photo_lib /
       canvas_file / asset_files) into the SERVER copy of the release; the
       client snapshot `updated` predates them, so a wholesale replace here
       silently wiped every upload since page load. The cover-artwork flow
       made it deterministic: upload → immediate section save → asset_files
       gone → stream 404 → blank artwork. These four fields are SERVER-
       MANAGED (the client never edits them through this path), so always
       carry them over from the fresh server copy. */
    const srv = arr[idx] as unknown as Record<string, unknown>;
    const merged = { ...updated } as unknown as Record<string, unknown>;
    for (const k of ['audio_tracks', 'photo_lib', 'canvas_file', 'asset_files']) {
      if (srv[k] !== undefined) merged[k] = srv[k];
    }
    arr[idx] = merged as unknown as Release;
  } else arr.push(updated);
  try {
    await apiPut('auth', '/api/data/releases', { value: arr });
    return true;
  } catch {
    return false;
  }
}

/** Create a new release (mirrors releases-wiring.js new-release default: id
 *  `r_<ts>`, unreleased, created_at) and append it to the blob. Returns the
 *  created object so the caller can navigate to its detail page, or NULL if the
 *  save failed, so the caller doesn't navigate to a release the server never
 *  stored. */
export async function createRelease(name: string, type: string): Promise<Release | null> {
  const rel: Release = {
    id: 'r_' + Date.now(),
    name: name || 'Untitled',
    type: (type || 'single').toLowerCase(),
    release_date: '',
    unreleased: true,
    created_at: new Date().toISOString(),
    checklist: [], assets: [], details: {}, timeline: [], links: {},
  };
  const ok = await persistRelease(rel);
  return ok ? rel : null;
}

/** Cancel/delete a release, drop it from the blob + best-effort untag any
 *  meta_campaign_tags that point at it (mirrors release-focused-wiring.js
 *  cancelReleaseAndExit). Returns false if the release wasn't in the blob. */
export async function cancelRelease(release: Release): Promise<boolean> {
  const arr = await readReleasesArray();
  const filtered = arr.filter((x) => String(x.id) !== String(release.id));
  if (filtered.length === arr.length) return false; // not found, wrong scope
  try { await apiPut('auth', '/api/data/releases', { value: filtered }); } catch { return false; }
  // best-effort: clear campaign tags pointing at this release
  try {
    const tj = await apiGet<{ value?: Record<string, unknown>; data?: Record<string, unknown> }>('auth', '/api/data/meta_campaign_tags').catch(() => null);
    const map: Record<string, unknown> = (tj && (tj.value || tj.data || (tj as Record<string, unknown>))) || {};
    let changed = false;
    for (const cid of Object.keys(map)) {
      const rec = map[cid];
      const trid = rec && typeof rec === 'object' ? (rec as { release_id?: unknown }).release_id : rec;
      if (String(trid) === String(release.id)) { delete map[cid]; changed = true; }
    }
    if (changed) await apiPut('auth', '/api/data/meta_campaign_tags', { value: map });
  } catch { /* swallow, tag cleanup is best-effort */ }
  return true;
}

/** The built-in "standard rollout" task preset (Triple-8), STANDARD_CHECKLIST
 *  from release-focused-wiring.js, flattened into checklist items. */
const STANDARD_CHECKLIST: ReadonlyArray<{ phase: string; items: readonly string[] }> = [
  { phase: 'Assets & Creative', items: [
    'Final master + alt mixes uploaded', 'Cover artwork final (3000×3000 @ 300 DPI)',
    'Square + 9:16 vertical artwork variants', 'Lyric sheet · ISRC · songwriting splits filed',
    'Hero photo + bio photos updated', 'Press release drafted + approved'] },
  { phase: 'Distribution & DSPs', items: [
    'Distributor delivery scheduled (DistroKid / UnitedMasters / etc.)', 'Spotify for Artists pitch submitted (4 weeks out)',
    'Apple Music for Artists pitch submitted',
    'Linkfire / Feature.fm smart link configured', 'YouTube canvas + audio video scheduled'] },
  { phase: 'Marketing', items: [
    'Announcement post locked (with embargo time)', 'Teaser clip(s) ready for IG / TikTok / YT Shorts',
    'Email blast queued in broadcast composer', 'Meta ad creative + audience approved',
    'Press list outreach started', 'Influencer / UGC seeds shipped'] },
  { phase: 'Release Day', items: [
    'Smart link live across DSPs', 'Announcement post fires at 12:01 AM ET',
    'Story chain across IG · TikTok · YT', 'Email broadcast sends (10am local)',
    'Spotify / Apple playlist add confirmations logged', 'Day-1 numbers screenshot + saved'] },
];
function newChecklistId(): string { return 'cl-' + Math.random().toString(36).slice(2, 10); }
/** Flat checklist items for the standard preset (verbatim labels + phases). */
export function standardChecklistItems(): ChecklistItem[] {
  const out: ChecklistItem[] = [];
  for (const p of STANDARD_CHECKLIST) {
    for (const label of p.items) {
      out.push({ id: newChecklistId(), phase: p.phase, label, done: false, assignee: '', due: '', status: 'not_started' } as ChecklistItem);
    }
  }
  return out;
}
/** A single new blank task (mirrors the legacy add-task shape). */
export function newChecklistTask(label: string): ChecklistItem {
  return { id: newChecklistId(), label, done: false, assignee: '', due: '', status: 'not_started' } as ChecklistItem;
}

// ── Task presets (GET /api/data/task_presets) ─────────────────────────────────
//
// Band-shared reusable to-do groups (release-focused-wiring.js `_presets` /
// window.acReleasePresets). Each preset is `{ id, name, tasks:[{ label, phase? }] }`.
// The "+ Add preset" picker reads them and applyPresetsToChecklist() appends the
// picked presets' tasks onto a release's checklist (mirrors acReleasePresets.applyByIds).

/** One reusable task preset (release-focused-wiring.js preset shape). */
export interface TaskPreset {
  readonly id?: string;
  readonly name?: string;
  readonly tasks?: ReadonlyArray<{ readonly label?: string; readonly phase?: string }>;
}

/** GET /api/data/task_presets → TaskPreset[]. Unwrap mirrors the other release
 *  helpers (releaseshub-wiring-v2.js / _presetsLoad's `_unwrapForRelease`):
 *  value → data → json, then `.value` if nested. Returns [] on any failure. */
export async function fetchTaskPresets(): Promise<TaskPreset[]> {
  let json: ReleasesResponse | null = null;
  try {
    json = await apiGet<ReleasesResponse>('auth', '/api/data/task_presets');
  } catch {
    return [];
  }
  let raw: unknown =
    json && (json.value !== undefined ? json.value : (json.data !== undefined ? json.data : json));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) raw = [];
  return raw as TaskPreset[];
}

/**
 * Append the tasks of the picked presets onto `release.checklist`, returning a
 * NEW release object (the blob is read-only). Mirrors acReleasePresets.applyByIds
 * (release-focused-wiring.js :1846): each preset task becomes a fresh checklist
 * item (`cl-<rand>`, not done, not_started). Unknown ids are ignored.
 */
export function applyPresetsToChecklist(
  release: Release,
  presets: ReadonlyArray<TaskPreset>,
  pickedIds: ReadonlyArray<string>,
): Release {
  const ids = new Set(pickedIds.map((x) => String(x)));
  const added: ChecklistItem[] = [];
  for (const p of presets) {
    if (!p || !ids.has(String(p.id)) || !Array.isArray(p.tasks)) continue;
    for (const t of p.tasks) {
      if (!t || !t.label) continue;
      added.push({
        id: newChecklistId(),
        label: t.label,
        phase: t.phase || '',
        done: false,
        assignee: '',
        due: '',
        status: 'not_started',
      } as ChecklistItem);
    }
  }
  const checklist = [...(Array.isArray(release.checklist) ? release.checklist : []), ...added];
  return { ...release, checklist };
}

// ── React hook (band-scoped stale-while-revalidate, instant on tab switch) ────
export function useReleases(): { releases: Release[]; loading: boolean; reload: () => void } {
  const { data, loading, reload } = useCachedData('releases', fetchReleases);
  return { releases: data ?? [], loading, reload };
}
